import { FormEvent, forwardRef, ReactElement, Ref } from "react";
import { Box, Dialog, DialogContent, Slide } from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import { useTranslation } from "next-i18next";
import { EnumLeverageProductsIndication } from "src/api/client/extended-search.api.types";
import { IKeyFigureGroup, ITags, ITagsData } from "src/api/server/extended-search.types";
import { MobileResultsButton } from "src/components/pages/search/components/filter-section/mobile-results-button";
import { MobileSearchFilterCloseButton } from "src/components/pages/search/components/filter-section/mobile-search-filter/mobile-search-filter-close-button";
import { InvestableInCountrySwitch } from "src/components/pages/search/components/investable-in-country-switch";
import { handleIsInvestable } from "src/components/pages/search/store/actions";
import { useSearchStore } from "src/components/pages/search/store/search-store";
import { Button } from "src/components-mui/base/button/button";
import { SwitchWithLabel } from "src/components-mui/base/switch-with-label/switch-with-label";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { EnumCountryKey } from "src/types/common-enum";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { KeyFiguresFilter } from "./key-figures-filter";
import { PopularFilters } from "./popular-filters";
import { TagsFilter } from "./tags-filter";

const Transition = forwardRef(function Transition(props: TransitionProps & { children: ReactElement }, ref: Ref<unknown>) {
    return <Slide direction="down" ref={ref} {...props} />;
});

interface IMobileSearchFilterProps {
    isOpen: boolean;
    isLoading: boolean;
    resultCount: number | null;
    tagsData: ITagsData;
    popularKeyFiguresGroup: IKeyFigureGroup;
    keyFiguresGroups: ReadonlyArray<IKeyFigureGroup>;
    popularTags: ITags;
    onClose: () => void;
    onReset: () => void;
}

export const MobileSearchFilter = ({
    isOpen,
    isLoading,
    resultCount,
    tagsData,
    popularKeyFiguresGroup,
    keyFiguresGroups,
    popularTags,
    onClose,
    onReset,
}: IMobileSearchFilterProps) => {
    const { t } = useTranslation(["extended-search", "common"]);
    const { country } = useRouterLocale();
    const { formatNumber } = useNumberFormatter();

    const { query, isInvestable, brokersIds, tags, keyFigures, isAtInvestable, isChInvestable, leverageProductsIndication } = useSearchStore();
    const isAnyFilterApplied = [
        query,
        country === EnumCountryKey.INT ? isInvestable : !isInvestable,
        brokersIds.length,
        tags.length,
        keyFigures.length,
        country === EnumCountryKey.AT ? !isAtInvestable : isAtInvestable,
        country === EnumCountryKey.CH ? !isChInvestable : isChInvestable,
        leverageProductsIndication !== EnumLeverageProductsIndication.AllWikifolios,
    ].some(Boolean);

    const isInvestableFilterVisible = country !== EnumCountryKey.INT;

    const getShowResultButtonText = () => {
        if (resultCount === null) {
            return t("buttons.show-all-results");
        }
        if (resultCount === 1) {
            return t("buttons.show-1-result");
        }
        return t("buttons.show-XX-results", { resultCount: formatNumber(resultCount) });
    };

    const onChangeInvestable = (event: FormEvent<HTMLInputElement>) => {
        const isChecked = event.currentTarget.checked;
        handleIsInvestable({ isChecked });
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullScreen
            scroll="paper"
            slots={{ transition: Transition }}
            slotProps={{
                paper: {
                    sx: { m: 0, borderRadius: 0, maxWidth: "100%", width: "100vw", position: "relative", backgroundColor: "gray.50" },
                },
            }}
        >
            <Box
                sx={{
                    px: 3,
                    py: 1,
                    position: "relative",
                    minHeight: "56px",
                    borderBottom: "1px solid",
                    borderColor: "gray.100",
                    backgroundColor: "white",
                }}
            >
                {isAnyFilterApplied && (
                    <Button variant="ghost-underline" size="small" onClick={onReset} sx={{ px: 0, position: "absolute" }}>
                        {t("buttons.reset-search-criteria")}
                    </Button>
                )}
                <MobileSearchFilterCloseButton
                    onClose={onClose}
                    sx={{
                        position: "absolute",
                        right: "16px",
                        top: "8px",
                    }}
                />
            </Box>
            <DialogContent
                sx={{
                    p: 0,
                    backgroundColor: "gray.50",
                    flex: 1,
                    overflowY: "auto",
                }}
            >
                {isInvestableFilterVisible && (
                    <Box
                        sx={{
                            mb: 1,
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                            bgcolor: "white",
                        }}
                    >
                        <SwitchWithLabel
                            checked={isInvestable}
                            onChange={onChangeInvestable}
                            label={t("labels.investable-wikifolios-only", { ns: "common" })}
                            sx={{
                                flexDirection: "row-reverse",
                                justifyContent: "space-between",
                                fontWeight: "semibold",
                            }}
                        />
                        <InvestableInCountrySwitch
                            size="medium"
                            fontSize="md"
                            sx={{ flexDirection: "row-reverse", justifyContent: "space-between", fontWeight: "semibold" }}
                        />
                    </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 0, py: 0 }}>
                    <PopularFilters popularKeyFiguresGroup={popularKeyFiguresGroup} tagsData={popularTags} />
                    <KeyFiguresFilter keyFiguresGroups={keyFiguresGroups} numberOfSelectedKeyFigures={keyFigures.length} />
                    <TagsFilter tagsData={tagsData} numberOfSelectedTags={tags.length} />
                </Box>
            </DialogContent>
            <Box
                sx={{
                    p: 3,
                    borderTop: "1px solid",
                    borderColor: "gray.100",
                    backgroundColor: "white",
                }}
            >
                <MobileResultsButton color="gray" onClick={onClose} isLoading={isLoading} sx={{ fontWeight: "bold", width: "100%" }}>
                    {getShowResultButtonText()}
                </MobileResultsButton>
            </Box>
        </Dialog>
    );
};
